import { getPublicKeys } from '@web-solutions/react-billing';

// Shared config for the Stripe intro one-time charge that wraps the
// trial-then-subscription flow. Today's actual charge ($0.99) is set up here
// and consumed by:
//   - PaymentProcessor (charges the intro via createOneClickPayment)
//   - ProductInfo / SimpleView (renders breakdown in the paywall modal)
//   - ApplePayButton (shows correct today amount in Apple Pay sheet)
export const STRIPE_INTRO_PAYMENT = {
  amount: 0.99,
  currency: 'USD',
  productCode: 'FoodZee Ethnicity Trial',
  productId: 'FoodZee Ethnicity Trial',
  priceId: 'price_1TWyoTGhKvuDkyq1sxflupm3',
  trialPeriodDays: '3',
  trigger: 'stripe_intro_trial',
} as const;

// Stripe subscriptions don't expose the intro/trial price on the product
// itself (we charge it via a separate one-time payment + trial_period_days).
// To render the breakdown UI we need to inject those fields on the fly.
// Try several fields to find the subscription's regular price. Remote-config
// often has `amount: "0.00"` (placeholder), so we can't trust the nullish
// coalescing alone — we prefer the value from Stripe (priceAmount) and fall
// back to anything numeric we can extract from the product.
const resolveFullAmount = (product: Record<string, any>): number => {
  const candidates = [
    product.priceAmount,
    product.amount,
    product.trialPriceAmount, // sometimes only trial info is present
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  // Last resort: parse a "$24.99" style string from textLineThrough/priceText.
  for (const s of [product.textLineThrough, product.priceText, product.price]) {
    if (typeof s !== 'string') continue;
    const parsed = Number(s.replace(/[^\d.]/g, ''));
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return NaN;
};

export const enrichStripeIntroProduct = <P extends Record<string, any>>(
  product: P | undefined | null,
  overrideIntroAmount?: number,
): P | undefined | null => {
  if (!product) return product;

  const introAmount = overrideIntroAmount ?? STRIPE_INTRO_PAYMENT.amount;
  const fullAmount = resolveFullAmount(product);

  if (!Number.isFinite(fullAmount) || fullAmount < introAmount) {
    return product;
  }

  const saved = Math.round((fullAmount - introAmount) * 100) / 100;
  const discountPct = Math.round((saved / fullAmount) * 100);

  return {
    ...product,
    isTrial: true,
    isIntroductory: true,
    trialPrice: `$${introAmount.toFixed(2)}`,
    trialPriceAmount: introAmount,
    trial_price_amount: String(introAmount),
    discount: discountPct,
    savedPrice: `$${saved.toFixed(2)}`,
  };
};

type StripePaymentIntent = {
  id?: string;
  status?: string;
};

export const confirmStripePaymentIntent = async (clientSecret?: string): Promise<StripePaymentIntent | null> => {
  if (!clientSecret) {
    return null;
  }

  const stripePublicKey = getPublicKeys()?.stripe?.public_key;
  const stripeFactory = (window as any).Stripe;

  if (!stripePublicKey || !stripeFactory) {
    throw new Error('Stripe is not initialized');
  }

  const stripe = stripeFactory(stripePublicKey);
  const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);

  if (error) {
    throw error;
  }

  return paymentIntent || null;
};
